import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Share2, Users, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { renderIdCard, inviteUrlFor } from "@/lib/idCard";
import { photoSrc } from "@/components/ProfilePhoto";
import { badgeFor } from "@/components/Badges";
import { ShareButtons } from "@/components/ShareButtons";

const shareText = (user) =>
  `I've got my One Talanta ID (${user.talanta_id}) on Talanta Hela — Kenya's national talent economy. Join me:`;

export const ShareIdCard = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState("");
  const qrWrap = useRef(null);
  const inviteUrl = inviteUrlFor(user.talanta_id);

  useEffect(() => {
    if (!open) return;
    setDataUrl("");
    const t = setTimeout(async () => {
      try {
        const url = await renderIdCard({ user, qrCanvas: qrWrap.current?.querySelector("canvas"), photoUrl: photoSrc(user), badge: badgeFor(user.invites) });
        setDataUrl(url);
      } catch (e) {
        toast.error("Could not render your ID card");
      }
    }, 60);
    return () => clearTimeout(t);
  }, [open, user]);

  return (
    <>
      <button
        data-testid="share-id-btn"
        onClick={() => setOpen(true)}
        className="w-full rounded-xl bg-talanta-gold/15 border border-talanta-gold/40 py-2.5 text-sm font-semibold text-talanta-green hover:bg-talanta-gold/25 flex items-center justify-center gap-2 transition-colors"
      >
        <Share2 className="h-4 w-4" /> Share my ID
        {user.invites > 0 && (
          <span data-testid="share-invites-count" className="ml-1 inline-flex items-center gap-1 rounded-full bg-talanta-green text-white px-2 py-0.5 text-[11px]">
            <Users className="h-3 w-3" /> {user.invites}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-id-dialog" className="sm:max-w-md p-0 overflow-hidden border-0 bg-talanta-cream max-h-[92vh] overflow-y-auto">
          <DialogTitle className="sr-only">Share your One Talanta ID</DialogTitle>
          <DialogDescription className="sr-only">Download or share your branded ID card and invite friends to Talanta Hela.</DialogDescription>
          <div className="kenya-stripe h-1.5" />
          <div className="px-5 pt-5 pb-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-talanta-terracotta">Invite friends</div>
            <h2 className="mt-1 font-display font-bold text-xl text-talanta-green">Your shareable ID card</h2>
            <p className="mt-1 text-sm text-talanta-charcoal/60">Friends who scan or tap your link land on Talanta Hela and get their own One Talanta ID.</p>
          </div>
          <div className="px-5">
            <div className="aspect-square w-full rounded-2xl overflow-hidden bg-talanta-green shadow-xl shadow-black/20 flex items-center justify-center">
              {dataUrl ? (
                <img data-testid="share-id-preview" src={dataUrl} alt={`One Talanta ID card for ${user.name || user.talanta_id}`} className="h-full w-full object-cover" />
              ) : (
                <Loader2 className="h-8 w-8 text-talanta-gold animate-spin" />
              )}
            </div>
            <div ref={qrWrap} className="hidden">
              <QRCodeCanvas value={inviteUrl} size={512} level="M" fgColor="#083323" bgColor="#FFFFFF" />
            </div>
          </div>
          <div className="px-5 py-5 space-y-3">
            <ShareButtons dataUrl={dataUrl} fileName={`one-talanta-id-${user.talanta_id}.png`} text={shareText(user)} url={inviteUrl} title="My One Talanta ID" />
            <p data-testid="share-invite-url" className="text-center text-[11px] text-talanta-charcoal/40 break-all">{inviteUrl}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
